/**
 * The member hub's Notifications tab (Member Hub design 1c) — the pure rules: the filter chips
 * and the query each sends, the mark a DealHunt event wears, where a review verdict links, and
 * the "Notify me" grid's rows. No fetch, no React; unit-tested in
 * `tests/community-hub-notifications.spec.tsx`.
 *
 * ONE feed: commentHub's bell feed (`GET /users/me/notifications`). DealHunt's owner events
 * arrive in it as `site_event` (DealHunt's bell drain, `server/services/ownerNotes/bell.ts`) —
 * never merged client-side with DealHunt's `/me` notifications.
 */
import type { CommentHubNotificationGroup } from 'shared-code/commenthub-api'
import type { NotificationsFilter } from 'shared-components/hooks/use-notifications'
import {
  notificationLine,
  type NotificationLine,
} from 'shared-components/comments/notification-copy'
import { noteAnchorId } from 'lib/owner-notes/band-copy'
import type { OwnerSettings } from 'lib/dealhunt/types'
import { accountNotificationLinks } from 'shared-components/account/routes'
import { NOTIFY_SETTINGS_ANCHOR, type EmailChoice } from 'shared-components/account/email-choice'
import { memberHubHref } from './model'

// ── the feed's filters ────────────────────────────────────────────────────────

export type HubFeedFilterId = 'all' | 'unread'

export interface HubFeedFilter {
  id: HubFeedFilterId
  label: string
  /** The server query the tab sends — filters, never a sort (the feed is newest-first). */
  filter: NotificationsFilter
}

/** The type commentHub files every DealHunt event under. */
export const SITE_EVENT_TYPE = 'site_event'

/**
 * The tabs (design 1b, 2026-10-28): All and Unread. Unlike the bell's tray, "Unread" IS a server
 * query here: the tab never moves the seen watermark on open (only "Mark all read" does), so
 * `unread=true` still returns what the member has not seen.
 */
export const HUB_FEED_FILTERS: readonly HubFeedFilter[] = [
  { id: 'all', label: 'All', filter: {} },
  { id: 'unread', label: 'Unread', filter: { unread: true } },
]

/** The empty list's copy, by why it is empty. */
export function hubFeedEmpty(id: HubFeedFilterId): string {
  return id === 'unread'
    ? "You're all caught up."
    : 'Nothing here yet. Replies, review checks and badges show up here.'
}

// ── one row ───────────────────────────────────────────────────────────────────

/** A moderator's verdict on a review or its photo — DealHunt's verdict family. */
export const NOTE_VERDICT_KINDS: ReadonlySet<string> = new Set([
  'note_published',
  'note_rejected',
  'note_removed',
  'note_hidden',
  'note_restored',
  'photo_verified',
  'photo_rejected',
])

export type SiteEventTone = 'ok' | 'alert' | 'award' | 'question' | 'info'

const KIND_TONES: Record<string, SiteEventTone> = {
  note_published: 'ok',
  note_restored: 'ok',
  photo_verified: 'ok',
  discussion_photo_approved: 'ok',
  profile_claimed: 'ok',
  note_rejected: 'alert',
  note_removed: 'alert',
  note_hidden: 'alert',
  photo_rejected: 'alert',
  discussion_photo_rejected: 'alert',
  founding_assigned: 'award',
  founding_member_assigned: 'award',
  conversation_starter_assigned: 'award',
  first_responder_assigned: 'award',
  note_views_milestone: 'award',
  question_received: 'question',
  /** SoundGuys' AI proofread the author's live review for typos — news, not a verdict. */
  note_polished: 'info',
}

const TONE_GLYPHS: Record<SiteEventTone, string> = {
  ok: '✓',
  alert: '!',
  award: '★',
  question: '?',
  info: 'i',
}

/** `meta.kind` of a DealHunt event; null for any other type or a missing kind. */
export function siteEventKind(group: CommentHubNotificationGroup): string | null {
  if (group.type !== SITE_EVENT_TYPE) {
    return null
  }
  const kind = group.meta?.kind
  return typeof kind === 'string' && kind ? kind : null
}

/**
 * The mark a DealHunt event wears, by `meta.kind`: ✓ published/verified/approved/restored, !
 * rejected/removed/hidden, ★ badges and milestones, ? a question; anything else (the recap, the
 * six-month nudge, a support reply, a kind this build does not know) a neutral "i". Null for a
 * commentHub type: those keep the tray's faces and star.
 */
export function siteEventMark(
  group: CommentHubNotificationGroup
): { tone: SiteEventTone; glyph: string } | null {
  if (group.type !== SITE_EVENT_TYPE) {
    return null
  }
  const tone = KIND_TONES[siteEventKind(group) ?? ''] ?? 'info'
  return { tone, glyph: TONE_GLYPHS[tone] }
}

/**
 * A review verdict that names its note links the member's Gear row (`…/gear#note-<id>`, Directive
 * 7d112c9a) — where the status and the moderator's reason already are — rather than repeating the
 * reason. Undefined for anything else (the row keeps its own link). ONE mapping for the tab's
 * rows and the bell dropdown's (`memberNotificationLinks().itemHref`).
 */
export function gearVerdictHref(
  group: CommentHubNotificationGroup,
  handle: string,
  trailingSlash: boolean
): string | undefined {
  const kind = siteEventKind(group)
  const noteId = group.meta?.note_id
  if (kind && NOTE_VERDICT_KINDS.has(kind) && typeof noteId === 'string' && noteId) {
    return `${memberHubHref(handle, trailingSlash, 'gear')}#${noteAnchorId(noteId)}`
  }
  return undefined
}

/** The tab row's line: the bell's own (`notificationLine`), with `gearVerdictHref`'s link. */
export function hubNotificationLine(
  group: CommentHubNotificationGroup,
  context: { handle: string; trailingSlash: boolean; appUrl?: string | null }
): NotificationLine {
  const line = notificationLine(group, { appUrl: context.appUrl })
  const gear = gearVerdictHref(group, context.handle, context.trailingSlash)
  return gear ? { ...line, href: gear } : line
}

// ── "Email me about" on SoundGuys ─────────────────────────────────────────────

/**
 * The one email choice (design 1b) writes two stores here: commentHub's `emailTypes` (the shared
 * `setEmailChoice`) and DealHunt's owner email keys, which ride the same three options — a
 * question about gear you own is a conversation; a reader milestone is recognition. The review
 * or photo check (`email_note_updates`) is an account notice and stays on under every choice; it
 * has no switch any more.
 */
export const HUB_EMAIL_CHOICE_DETAILS: Readonly<Record<EmailChoice, string>> = {
  replies:
    'Recommended. Replies to you, new comments where you joined, questions about gear you own.',
  all: 'Also likes and pinned comments, and reader milestones on your owner reviews.',
  account: 'Pauses everything else. Review and photo checks are always emailed.',
}

/** DealHunt's email keys as a choice sets them (`PATCH /me/settings`, one call). */
export function ownerEmailFor(
  choice: EmailChoice
): Pick<OwnerSettings, 'email_note_updates' | 'email_questions' | 'email_view_milestones'> {
  return {
    // "Review and photo checks are always emailed": every pick turns it (back) on, so the
    // promise holds even for an owner who had switched it off on My gear.
    email_note_updates: true,
    email_questions: choice !== 'account',
    email_view_milestones: choice === 'all',
  }
}

/** The choice DealHunt's two keys amount to — the higher one, like `emailChoiceOf` (a mixed state
 *  normalises on the next pick); null without an owner profile. */
export function ownerEmailChoiceOf(
  settings: Partial<OwnerSettings> | null | undefined
): EmailChoice | null {
  if (!settings || typeof settings.email_questions !== 'boolean') {
    return null
  }
  if (settings.email_view_milestones) {
    return 'all'
  }
  return settings.email_questions ? 'replies' : 'account'
}

/** commentHub's cadence for comment email (its runbook `docs/reader-notification-email.md`). */
export const COMMENT_EMAIL_CADENCE =
  "Comment email arrives at most once an hour, grouping what's new."

/** The only frequency rule DealHunt enforces (`OWNER_VIEWS_EMAIL_WINDOW_DAYS`): said once, on
 *  the footnote, now that the milestone row has no line of its own. */
export const HUB_EMAIL_FOOTNOTE: readonly string[] = [
  'Milestone emails: at most one a week per review.',
]

// ── "Products you follow" ─────────────────────────────────────────────────────

/** The followed-product emails v1 switches (`docs/product-follow.md` § Email preferences). */
export type FollowEmailKey = 'email_follow_coverage' | 'email_follow_owner_reviews'

export interface FollowNotifyRow {
  key: FollowEmailKey
  label: string
}

/**
 * One switch per v1 event kind, under the email choice: email only (DealHunt never bells them),
 * so the choice doesn't set them. `email_follow_successor` gets its row ('A newer model is
 * announced or reviewed') in v2.
 */
export const FOLLOW_NOTIFY_ROWS: readonly FollowNotifyRow[] = [
  { key: 'email_follow_coverage', label: 'New SoundGuys coverage' },
  { key: 'email_follow_owner_reviews', label: 'New owner reviews' },
]

/** The rows this DealHunt carries a key for, with the stored value; none without settings. */
export function followEmailRows(
  settings: Partial<OwnerSettings> | null | undefined
): { row: FollowNotifyRow; on: boolean }[] {
  return FOLLOW_NOTIFY_ROWS.flatMap(row => {
    const on = settings?.[row.key]
    return typeof on === 'boolean' ? [{ row, on }] : []
  })
}

// ── the bell's links ──────────────────────────────────────────────────────────

/** The email box's anchor on the Notifications tab — the bell's "Settings" lands on it. One value
 *  with the account pages' (`account/email-choice`): the same link works on both. */
export { NOTIFY_SETTINGS_ANCHOR }

/** The bell's links (HeaderAuth `notificationLinks`). */
export interface MemberNotificationLinks {
  settings: string
  all: string
  itemHref: (group: CommentHubNotificationGroup) => string | undefined
}

/**
 * Where the bell's "Settings", "See all" and a review verdict's row go (design 1e: "its Settings link opens 1c instead of
 * an inline panel"): the member's Notifications tab — only when they HAVE a member page (signed
 * in, profile not switched off). Null otherwise, and the bell keeps its inline settings.
 */
export function memberNotificationLinks(
  me: {
    profile: { handle: string; claimed: boolean }
    settings?: { profile_public?: boolean } | null
  } | null,
  trailingSlash: boolean
): MemberNotificationLinks | null {
  if (!me || !me.profile.claimed || me.settings?.profile_public === false) {
    return null
  }
  const { handle } = me.profile
  const all = memberHubHref(handle, trailingSlash, 'notifications')
  return {
    settings: `${all}#${NOTIFY_SETTINGS_ANCHOR}`,
    all,
    itemHref: group => gearVerdictHref(group, handle, trailingSlash),
  }
}

/**
 * The bell's links in the header: the member's hub tab when they have a member page
 * (`memberNotificationLinks`), else the account Notifications page (plan D1/D7) — which forwards
 * a member whose `/me` the header has not read yet. Never undefined: the tray's inline settings
 * are gone on SoundGuys since every reader has a Notifications page.
 */
export function bellNotificationLinks(
  member: MemberNotificationLinks | null | undefined,
  profileHref: string
): MemberNotificationLinks | { settings: string; all: string } {
  return member ?? accountNotificationLinks(profileHref)
}
